import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import "./IntroLayout.css";

const PlusIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h6" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="19" y1="16" x2="19" y2="22" />
    <line x1="16" y1="19" x2="22" y2="19" />
  </svg>
);

const ListIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="4" y="2" width="16" height="20" rx="2" />
    <line x1="8" y1="8" x2="16" y2="8" />
    <line x1="8" y1="12" x2="16" y2="12" />
    <line x1="8" y1="16" x2="13" y2="16" />
  </svg>
);

const DepotIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 9l9-6 9 6v11a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z" />
    <polyline points="9 21 9 13 15 13 15 21" />
  </svg>
);

const ArrowIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

// Ids whose plural form has no "s" in the existing intro texts
const SINGULAR_IDS = [
  "devis",
  "facture_d_avoir",
  "facture_achat",
  "facture_depense",
  "plan_comptable",
];

// Per-page button labels [add, list]; generic fallback below
const BUTTON_LABELS = {
  articles: ["Créer un article", "Voir les articles"],
  clients: ["Créer un client", "Voir les clients"],
  fournisseurs: ["Créer un fournisseur", "Voir les fournisseurs"],
  invoices: ["Créer une facture", "Voir les factures"],
  devis: ["Créer un devis", "Voir les devis"],
  facture_d_avoir: ["Créer une facture d'avoir", "Voir les factures d'avoir"],
  facture_achat: ["Créer une facture", "Voir les factures"],
  facture_depense: ["Créer une dépense", "Voir les dépenses"],
};

// Per-page description of the list card
const LIST_DESCRIPTIONS = {
  Organisations: "Voir tout les organisations",
  articles: "Voir tout les articles",
  clients: "Voir tout les clients",
  invoices: "Voir toutes les factures",
  devis: "Voir tous les devis",
  facture_d_avoir: "Voir toutes les factures d'avoir",
  fournisseurs: "Voir tout les fournisseurs",
  facture_achat: "Voir toutes les factures d'achat liées au stock",
  facture_depense: "Voir toutes les factures de dépenses",
  plan_comptable: "Voir tout les Plan comptable",
  piece: "Voir tout les piéces",
  Devises: "Voir tout les devises",
  Taxes: "Voir tout les taxes",
  équipe: "Voir les membres de l'équipe",
  bq: "Voir tout les comptes comptable de la banque",
  cs: "Voir tous les comptes comptables de la caisse",
  history: "Voir tout l'historique de votre équipe",
};

const Proxy = (props) => {
  const [t] = useTranslation();

  const plural = SINGULAR_IDS.includes(props.id) ? "" : "s";
  const hasAdd = !!props.add && props.add !== "none";
  const hasList = !!props.list && props.list !== "none";
  const hasDepots = props.id === "articles" && !!props.listDepots;

  const headingName =
    props.id === "facture_d_avoir" ? t("f_avoir") : t(props.name);

  const addTitle = (() => {
    if (props.id === "plan_comptable") return t("Nouveau Compte");
    if (props.id === "facture_achat")
      return t("Nouvelle Facture d'achat liée au stock");
    if (props.id === "équipe") return t("Nouveau membre de l'équipe");
    if (props.id === "facture_depense") return t("Nouvelle Facture de depense");
    return t(
      props.genre === "feminin" ? "Nouvelle " + props.name : "Nouveau " + props.name
    );
  })();

  const addDesc =
    props.id === "équipe"
      ? t("Entrer les détails et créez des membres d'équipe maintenant")
      : t(`Entrer les détails et créez des ${props.name.toLowerCase()}${plural} maintenant`);

  const listTitle = t(`Liste des ${props.name}${plural}`);
  const listDesc = LIST_DESCRIPTIONS[props.id]
    ? t(LIST_DESCRIPTIONS[props.id])
    : "";

  const [addLabel, listLabel] = BUTTON_LABELS[props.id] || [
    t("Ajouter"),
    t("Voir la liste"),
  ];

  const cards = [];
  if (hasAdd) {
    cards.push({
      key: "add",
      to: props.add,
      icon: <PlusIcon />,
      title: addTitle,
      desc: addDesc,
      label: addLabel,
      primary: true,
    });
  }
  if (hasList) {
    cards.push({
      key: "list",
      to: props.list,
      icon: <ListIcon />,
      title: listTitle,
      desc: listDesc,
      label: listLabel,
    });
  }
  if (hasDepots) {
    cards.push({
      key: "depots",
      to: props.listDepots,
      icon: <DepotIcon />,
      title: t("Les " + props.nameDepots),
      desc: t("voir tout les " + props.nameDepots),
      label: t("Voir les " + props.nameDepots.toLowerCase()),
    });
  }

  return (
    <div className="intro-layout main-container container-fluid">
      <header className="intro-layout__header">
        <h1 className="intro-layout__title">{headingName}</h1>
        <p id="proxyComponentDesc" className="intro-layout__desc">
          {props.description}
        </p>
      </header>

      <div className="intro-layout__grid">
        {cards.map((card) => (
          <div className="intro-layout__card" key={card.key}>
            <div className="intro-layout__icon">{card.icon}</div>
            <div className="intro-layout__content">
              <h3 className="intro-layout__card-title">{card.title}</h3>
              <p className="intro-layout__card-desc">{card.desc}</p>
            </div>
            <Link
              to={card.to}
              className={`intro-layout__btn ${
                card.primary
                  ? "intro-layout__btn--primary"
                  : "intro-layout__btn--outline"
              }`}
            >
              {card.label} <ArrowIcon />
            </Link>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Proxy;

